import { computed, inject } from '@angular/core';

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { getAddress, isAddress } from 'ethers';

import { SUPPORTED_EVM_CHAINS } from '@drf/core';

import {
  WalletConnectionErrorCode,
  WalletConnectionErrorPayload,
  WalletIdentity,
  WalletIdentityState,
  WalletIdentityStatus,
} from '../models';
import { WalletAuthSessionStorageService, WalletAutoConnectPreferenceService } from '../services';
import { WALLET_CONNECTOR } from '../tokens';
import { WalletBalancesStore } from './wallet-balances.store';

const initialState: WalletIdentityState = {
  error: null,
  identity: null,
  status: WalletIdentityStatus.Idle,
  walletDataRefreshRevision: 0,
};

export const WalletIdentityStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    /** Reports the currently connected wallet chain id, if available. */
    chainId: computed(() => store.identity()?.chainId ?? null),
    /** Reports whether a supported wallet identity is currently connected. */
    isConnected: computed(() => {
      const status = store.status();

      return status === WalletIdentityStatus.Connected;
    }),
    /** Reports whether a user-triggered wallet connection request is pending. */
    isPending: computed(() => {
      const status = store.status();

      return status === WalletIdentityStatus.Connecting;
    }),
    /** Increments whenever wallet-dependent data should be reloaded by consumers. */
    needRefreshWalletData: computed(() => store.walletDataRefreshRevision()),
  })),
  withMethods((store, connector = inject(WALLET_CONNECTOR), supportedChains = inject(SUPPORTED_EVM_CHAINS)) => {
    const authSessionStorage = inject(WalletAuthSessionStorageService);
    const autoConnectPreference = inject(WalletAutoConnectPreferenceService);
    const walletBalancesStore = inject(WalletBalancesStore);
    let eventCleanups: Array<() => void> = [];
    let restorePromise: Promise<void> | null = null;

    const markWalletDataRefreshNeeded = (): { walletDataRefreshRevision: number } => ({
      walletDataRefreshRevision: store.walletDataRefreshRevision() + 1,
    });

    /** Treats Reown unsupported-chain disconnect as a full user disconnect. */
    const clearUnsupportedChainSession = (): void => {
      cleanupEvents();
      void connector.resetNetworkSelection().catch(() => undefined);
      void connector.disconnect().catch(() => undefined);
      autoConnectPreference.disableAutoConnect();
    };

    /** Reports whether a wallet event is disconnecting an established local session. */
    const shouldClearAuthSessionForWalletEvent = (): boolean => {
      const status = store.status();

      return status === WalletIdentityStatus.Connected || status === WalletIdentityStatus.UnsupportedChain;
    };

    /** Clears wallet identity while keeping an explicit disconnected state. */
    const setDisconnected = (options: { clearAuthSession?: boolean } = {}): void => {
      if (store.status() === WalletIdentityStatus.UnsupportedChain) {
        clearUnsupportedChainSession();
      }
      walletBalancesStore.clear();
      patchState(store, {
        error: null,
        identity: null,
        ...markWalletDataRefreshNeeded(),
        status: WalletIdentityStatus.Disconnected,
      });

      if (options.clearAuthSession) {
        authSessionStorage.clearSiwxTokens();
      }
    };

    /** Stores a normalized wallet error and optionally removes stale identity data. */
    const setError = (error: WalletConnectionErrorPayload, clearIdentity = true): void => {
      patchState(store, {
        error,
        identity: clearIdentity ? null : store.identity(),
        ...markWalletDataRefreshNeeded(),
        status: error.code === WalletConnectionErrorCode.UserRejected ? WalletIdentityStatus.Rejected : WalletIdentityStatus.Error,
      });
    };

    /** Stores identity and marks unsupported chains without hiding the selected wallet. */
    const setIdentity = (identity: WalletIdentity): void => {
      if (isSupportedChain(identity.chainId, supportedChains)) {
        const previousStatus = store.status();
        patchState(store, {
          error: null,
          identity,
          ...markWalletDataRefreshNeeded(),
          status: WalletIdentityStatus.Connected,
        });

        const shouldCloseNetworkSelectionModal = previousStatus === WalletIdentityStatus.UnsupportedChain;
        if (shouldCloseNetworkSelectionModal) {
          void connector.closeNetworkSelectionModal().catch(() => undefined);
        }

        return;
      }

      patchState(store, {
        error: {
          code: WalletConnectionErrorCode.UnsupportedChain,
          message: `Unsupported EVM chain: ${identity.chainId}`,
        },
        identity,
        ...markWalletDataRefreshNeeded(),
        status: WalletIdentityStatus.UnsupportedChain,
      });
    };

    /** Removes all active provider event subscriptions for the current session. */
    const cleanupEvents = (): void => {
      eventCleanups.forEach(cleanup => cleanup());
      eventCleanups = [];
    };

    /** Applies provider account changes to the current local wallet identity. */
    const handleAccountsChanged = (accounts: readonly string[]): void => {
      if (accounts.length === 0) {
        setDisconnected({ clearAuthSession: shouldClearAuthSessionForWalletEvent() });

        return;
      }

      const identity = store.identity();

      if (!identity || !isAddress(accounts[0])) {
        return;
      }

      const accountAddress = getAddress(accounts[0]);
      setIdentity({
        ...identity,
        accountAddress,
        ownerAddress: accountAddress,
      });
    };

    /** Applies provider chain changes while preserving the current wallet address. */
    const handleChainChanged = (chainIdHex: `0x${string}`): void => {
      const identity = store.identity();

      if (!identity) {
        return;
      }

      setIdentity({
        ...identity,
        chainId: Number.parseInt(chainIdHex, 16),
        chainIdHex: chainIdHex.toLowerCase() as `0x${string}`,
      });
    };

    /** Clears local wallet state when the provider reports a disconnect. */
    const handleDisconnect = (): void => {
      setDisconnected({ clearAuthSession: shouldClearAuthSessionForWalletEvent() });
    };

    /** Replaces provider event subscriptions with handlers bound to this store. */
    const registerEvents = (): void => {
      cleanupEvents();
      eventCleanups = [
        connector.onAccountsChanged(handleAccountsChanged),
        connector.onChainChanged(handleChainChanged),
        connector.onDisconnect(handleDisconnect),
      ];
    };

    return {
      /** Runs the user-triggered wallet connection flow. */
      async connect(): Promise<void> {
        if (!connector.isAvailable()) {
          patchState(store, {
            error: {
              code: WalletConnectionErrorCode.ProviderUnavailable,
              message: 'Wallet connection is unavailable.',
            },
            identity: null,
            ...markWalletDataRefreshNeeded(),
            status: WalletIdentityStatus.Unavailable,
          });

          return;
        }

        patchState(store, {
          error: null,
          ...markWalletDataRefreshNeeded(),
          status: WalletIdentityStatus.Connecting,
        });
        registerEvents();

        try {
          const identity = await connector.connect();
          autoConnectPreference.enableAutoConnect();
          setIdentity(identity);
        } catch (error) {
          setError(toWalletConnectionErrorPayload(error));
        }
      },

      /** Clears the local wallet session and suppresses silent restore until reconnect. */
      async disconnect(): Promise<void> {
        cleanupEvents();
        try {
          await connector.disconnect();
        } finally {
          autoConnectPreference.disableAutoConnect();
          setDisconnected({ clearAuthSession: true });
        }
      },

      handleAccountsChanged,

      handleChainChanged,

      handleDisconnect,

      /** Restores a previously authorized wallet without prompting the user. */
      init(): Promise<void> {
        if (store.status() === WalletIdentityStatus.Connected) {
          return Promise.resolve();
        }

        if (restorePromise) {
          return restorePromise;
        }

        restorePromise = restoreWallet().finally(() => {
          restorePromise = null;
        });

        return restorePromise;
      },
    };

    async function restoreWallet(): Promise<void> {
      if (!autoConnectPreference.isAutoConnectEnabled()) {
        await connector.disconnect();
        setDisconnected();

        return;
      }

      if (!connector.isAvailable()) {
        patchState(store, {
          error: null,
          identity: null,
          ...markWalletDataRefreshNeeded(),
          status: WalletIdentityStatus.Unavailable,
        });

        return;
      }

      registerEvents();
      patchState(store, {
        error: null,
        ...markWalletDataRefreshNeeded(),
        status: WalletIdentityStatus.Connecting,
      });

      try {
        const identity = await connector.getConnectedIdentity();

        if (!identity) {
          patchState(store, {
            error: null,
            identity: null,
            ...markWalletDataRefreshNeeded(),
            status: WalletIdentityStatus.Idle,
          });

          return;
        }

        setIdentity(identity);
      } catch (error) {
        setError(toWalletConnectionErrorPayload(error));
      }
    }
  }),
);

/** Checks whether frontend chain restrictions allow the active chain. */
function isSupportedChain(chainId: number, supportedChains: readonly { chainId: number }[]): boolean {
  return supportedChains.length === 0 || supportedChains.some(chain => chain.chainId === chainId);
}

/** Converts thrown connector errors into store-friendly error payloads. */
function toWalletConnectionErrorPayload(error: unknown): WalletConnectionErrorPayload {
  if (isWalletConnectionErrorPayload(error)) {
    return {
      code: error.code,
      message: error.message,
    };
  }

  if (error instanceof Error) {
    return {
      code: WalletConnectionErrorCode.Unknown,
      message: error.message,
    };
  }

  return {
    code: WalletConnectionErrorCode.Unknown,
    message: 'Wallet identity operation failed.',
  };
}

/** Detects normalized wallet errors without trusting arbitrary thrown values. */
function isWalletConnectionErrorPayload(error: unknown): error is WalletConnectionErrorPayload {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof error.message === 'string' &&
    Object.values(WalletConnectionErrorCode).includes(error.code as WalletConnectionErrorCode)
  );
}
